/**
 * OceanAssessment — post-OAuth onboarding for users who signed up via
 * Google and skipped the registration "Get to know Maya" chat.
 *
 * Reuses MayaProfileChat verbatim (same 22-question script: 12
 * preference answers + 10 OCEAN Likert statements) so the experience
 * matches email registration exactly. On completion, persists:
 *   • OCEAN answers → POST /api/ocean/submit
 *   • Preference fields → POST /api/profile/teen-preferences
 */
import React, { useEffect, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { IdleLoopVideo } from '../chat/IdleLoopVideo';
import MayaProfileChat from '../MayaProfileChat';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const WARM_BG = 'linear-gradient(135deg, #FFEDD8 0%, #F4E5F2 100%)';
const TEXT_PRIMARY = '#1A1209';
const TEXT_SECONDARY = '#7B6A55';

const OceanAssessment = ({ onComplete, onPostpone, mandatory = false }) => {
  const [phase, setPhase] = useState('intro'); // intro | chat | complete
  const [formData, setFormData] = useState({ oceanAnswers: {} });
  const [maya, setMaya] = useState({ name: 'Maya', avatar_url: '/clare-ava.png', idle_loop_url: null });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const introSpokenRef = useRef(false);

  const getHeaders = () => {
    const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  // Pull Maya's greeter (idle-loop video + avatar) on mount
  useEffect(() => {
    fetch(`${API_URL}/api/ava-video/registration-greeter`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d) setMaya({
          name: d.name || 'Maya',
          avatar_url: d.avatar_url || '/clare-ava.png',
          idle_loop_url: d.idle_loop_url || null,
        });
      })
      .catch(() => { /* keep fallback */ });
  }, []);

  // Intro narration — Maya reads the title + body paragraph, pauses
  // 2 seconds, then says "Let's go". Fires immediately; falls back to
  // first user gesture if Chrome's autoplay policy blocks it.
  useEffect(() => {
    if (phase !== 'intro') return;
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    const introLine =
      "Before we go to payments, I need to get to know you first. " +
      "I have a few quick questions to ask you — there are no wrong or right answers. " +
      "If you are not sure which one to choose, click on the one that feels right to you.";
    const ctaLine = "Let's go";

    const speakIntro = () => {
      introSpokenRef.current = true;
      try {
        window.speechSynthesis.cancel();
        const voices = window.speechSynthesis.getVoices() || [];
        const v =
          voices.find((vv) => /female|samantha|karen|moira|fiona|tessa|victoria/i.test(vv.name)) ||
          voices.find((vv) => vv.lang?.startsWith('en')) ||
          null;
        const u1 = new SpeechSynthesisUtterance(introLine);
        if (v) u1.voice = v;
        u1.rate = 0.95; u1.pitch = 1.05;
        u1.onend = () => {
          setTimeout(() => {
            const u2 = new SpeechSynthesisUtterance(ctaLine);
            if (v) u2.voice = v;
            u2.rate = 0.95; u2.pitch = 1.05;
            window.speechSynthesis.speak(u2);
          }, 2000);
        };
        window.speechSynthesis.speak(u1);
      } catch { /* noop */ }
    };

    // Try immediately. If voices aren't loaded yet, wait for the
    // voiceschanged event (Chrome lazy-loads voices).
    if ((window.speechSynthesis.getVoices() || []).length === 0) {
      const h = () => { speakIntro(); window.speechSynthesis.removeEventListener('voiceschanged', h); };
      window.speechSynthesis.addEventListener('voiceschanged', h);
    } else speakIntro();

    // Gesture-gated fallback for Chrome's autoplay policy.
    const onGesture = () => { if (!introSpokenRef.current) speakIntro(); };
    document.addEventListener('pointerdown', onGesture, { once: true, capture: true });
    document.addEventListener('keydown', onGesture, { once: true, capture: true });
    document.addEventListener('touchstart', onGesture, { once: true, capture: true });

    return () => {
      document.removeEventListener('pointerdown', onGesture, { capture: true });
      document.removeEventListener('keydown', onGesture, { capture: true });
      document.removeEventListener('touchstart', onGesture, { capture: true });
      // Speech carries naturally into the chat screen.
    };
  }, [phase]);  

  const handleAnswer = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const submitAll = async () => {
    setSubmitting(true); setError(null);
    try {
      // 1. Submit OCEAN answers
      const oceanMap = formData.oceanAnswers || {};
      const oceanAnswersArr = Object.entries(oceanMap).map(([qid, value]) => ({ qid, value }));
      if (oceanAnswersArr.length === 10) {
        await fetch(`${API_URL}/api/ocean/submit`, {
          method: 'POST', headers: getHeaders(),
          body: JSON.stringify({ answers: oceanAnswersArr }),
        });
      }
      // 2. Submit preference fields
      const prefFields = [
        'firstName', 'age', 'pronouns', 'favoriteColor', 'mainGoal',
        'preferredVibe', 'topHobby', 'musicGenre', 'favoriteMovie',
        'spiritAnimal', 'happyPlace', 'superpower',
      ];
      const prefsPayload = {};
      prefFields.forEach((f) => { if (formData[f]) prefsPayload[f] = String(formData[f]); });
      await fetch(`${API_URL}/api/profile/teen-preferences`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify(prefsPayload),
      });
      setPhase('complete');
      if (onComplete) onComplete();
    } catch (e) {
      setError('Something went wrong saving your answers. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePostpone = async () => {
    try {
      await fetch(`${API_URL}/api/ocean/postpone`, { method: 'POST', headers: getHeaders() });
    } catch { /* noop */ }
    if (onPostpone) onPostpone();
  };

  // ─── Intro Screen ───
  if (phase === 'intro') {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center" style={{ background: WARM_BG }} data-testid="ocean-intro">
        {!mandatory && (
          <button
            onClick={handlePostpone}
            aria-label="Close and remind me tomorrow"
            className="absolute right-4 p-2.5 rounded-full bg-white/70 hover:bg-white shadow-md transition-colors"
            style={{ top: 'calc(env(safe-area-inset-top, 0px) + 1rem)', color: TEXT_PRIMARY }}
            data-testid="ocean-intro-close"
          >
            <X size={18} />
          </button>
        )}
        <div className="text-center max-w-xl px-6">
          <div className="flex flex-col items-center mb-8" data-testid="ocean-maya-header">
            <div className="relative w-48 h-48 md:w-56 md:h-56 rounded-2xl overflow-hidden shadow-2xl ring-4 ring-white mb-4">
              {maya.idle_loop_url ? (
                <IdleLoopVideo
                  src={`${API_URL}/api/files/${maya.idle_loop_url}`}
                  poster={maya.avatar_url || undefined}
                  className="w-full h-full object-cover"
                  testId="maya-intro-idle-loop"
                />
              ) : (
                <img
                  src={maya.avatar_url || '/clare-ava.png'}
                  alt={maya.name}
                  className="w-full h-full object-cover maya-breathe"
                  onError={(e) => {
                    e.currentTarget.src = `https://api.dicebear.com/7.x/lorelei/svg?seed=${encodeURIComponent(maya.name || 'Maya')}&backgroundColor=c7d2fe`;
                  }}
                />
              )}
              <span
                className="absolute bottom-2 right-2 w-5 h-5 rounded-full border-2 border-white"
                style={{ backgroundColor: '#10B981' }}
              />
            </div>
            <p className="text-xl font-bold" style={{ color: TEXT_PRIMARY }}>{maya.name}</p>
            <p className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: '#7B4FD8' }}>
              Practice Manager
            </p>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-4 leading-snug" style={{ color: TEXT_PRIMARY }}>
            Before we go to payments, I need to get to know you first.
          </h1>
          <p className="text-sm sm:text-base mb-10 leading-relaxed" style={{ color: TEXT_SECONDARY }}>
            I have a few quick questions to ask you — there are no wrong or right answers. If you are not sure which one to choose, click on the one that feels right to you.
          </p>
          {mandatory && (
            <p className="text-xs font-semibold text-amber-700 mb-8 -mt-6" data-testid="ocean-mandatory-warning">
              ⚠️ Every question must be answered — failure to do so will prevent you from completing your registration.
            </p>
          )}
          <button
            onClick={() => setPhase('chat')}
            className="px-10 py-3.5 rounded-xl text-white text-sm font-semibold transition-transform hover:scale-[1.02] shadow-md"
            style={{ backgroundColor: '#7B4FD8' }}
            data-testid="ocean-start-btn"
          >
            Let&apos;s go
          </button>
          <div className="mt-6">
            {!mandatory && (
              <button onClick={handlePostpone} className="text-xs underline" style={{ color: '#9D8FB8' }} data-testid="ocean-postpone-btn">
                Remind me tomorrow
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── Chat Screen — full 22-question MayaProfileChat ───
  if (phase === 'chat') {
    return (
      <div className="fixed inset-0 z-[100] overflow-y-auto px-4 py-8" style={{ background: WARM_BG }} data-testid="ocean-questions">
        <div className="max-w-4xl mx-auto" data-testid="maya-profile-chat-wrapper">
          <div className="text-center mb-4">
            <h2 className="text-lg font-bold text-gray-900">Maya will guide you through the questions</h2>
            {mandatory && (
              <p className="mt-1 text-xs font-semibold text-amber-700" data-testid="ocean-chat-mandatory-warning">
                ⚠️ Every question must be answered — failure to do so will prevent you from completing your registration.
              </p>
            )}
          </div>
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800" data-testid="ocean-submit-error">
              {error}
            </div>
          )}
          <MayaProfileChat
            formData={formData}
            onAnswer={handleAnswer}
            onComplete={submitAll}
            onBack={() => setPhase('intro')}
          />
          {submitting && (
            <div className="mt-4 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#7B4FD8' }} />
              <span className="text-xs text-gray-500">Saving your profile…</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── Complete (instant transition handled by onComplete from parent) ───
  return null;
};

export default OceanAssessment;
